// @givmo/compliance — what changed in a filing's rule (CCM-OPS1-REPIN's G4; CCM-R1-REVIEW-CHANGES).
//
// The comparison the backend serves for a filing on the calendar whose rule was updated after the
// filing was pinned to it (`GET …/obligations/{obligation_id}/rule-change`,
// `ComplianceObligationRuleChangeOut`, be#2770). It carries:
// - the version the filing is pinned to and the rule's current one, each with the day Givmo's rule
//   took it (`loaded_on`);
// - the flag;
// - the closed set of aspects that moved (structure, with no words: cut 1008-11's D6);
// - each version's next worked-out date, per deadline;
// - for each deadline entry that keeps years, every year of the filing "Use the updated rule" moves,
//   overdue ones too, with its date under each version (`dates.year_moves`,
//   CCM-OPS1-REPIN-EVERY-YEAR). An older server serves none, and the comparison is then the next
//   dates alone, as before.
//
// Two ways in read it: the filing panel's notice (frames 103, 104) and the review's change card
// (frames 101, 102).
//
// ══ WHAT IT SAYS (D-CCM-51's N37 and N48; D-CCM-59's U1-a to U1-e) ═══════════════════════════════
//
// Every aspect of the backend's closed set (cut 1008-11's D6) has its words:
// - a due date the update moved, added or removed is a "Due date" row (U1-a), and an extension
//   added, removed or changed an "Extension" row (N37): each version's date, or N48 "Not in Givmo’s
//   rule" for the version without it (U1-b in "Now"). A row whose two sides read the same moved
//   nothing for this filing, so it isn't drawn: U1-a is for a date that moved;
// - a due date that keeps years is a "Due date" row for each year the update moves, oldest first,
//   each with the year it is about under its label (R1); one that moves none of this filing's years
//   is named by R2's word instead ("due date", D-CCM-107), so U1-d says the dates stay the same only
//   where no year moves;
// - the rest (wording, sources, fee, forms, conditions, how it’s sent, other details: U1-e) is one
//   line, under the table (U1-c) or, with no row, in its place (U1-d: the dates stay the same).
//
// A comparison is shown only where approved words say ALL of it. A dated aspect whose date isn't
// served, an aspect outside the set, a rule with no current version (U11), or a date's rule
// changed with nothing moved and nothing else to name, shows N36 alone. It never shows a partial
// "What changed" that leaves out what moved.
//
// Tolerant, as every decode in this module: an unknown field is ignored, a missing one is absent,
// and nothing throws.

import { displayComplianceDate, waitsOnTheFirstPeriod } from './compliance-decode';
import { decodeWorkedOutDate, type WorkedOutDate } from './compliance-filings-decode';
import { FIRST_PERIOD_WAITS_CELL, fiscalYearEndedLine } from './compliance-panel-model';
import { andList } from './compliance-submission-model';
import {
  DUE_DATE,
  DUE_DATE_ITEM,
  EXTENSION,
  NOT_IN_GIVMOS_RULE,
  RULE_ASPECT_WORDS,
} from './review-words';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function optionalStr(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}
function optionalInt(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) ? value : undefined;
}
function records(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

/** One version of a rule, as the comparison names it. */
export interface RuleVersionStamp {
  ruleVersionId: string;
  version: number;
  /** The day Givmo's rule took this version (N36's and N37's dates). */
  loadedOn: string;
}

/** One aspect that moved between the two versions (`extension_added`, `wording_changed`, …). */
export interface RuleChangeAspect {
  aspect: string;
  deadlineIndex?: number;
}

/** One open year of the filing whose due date "Use the updated rule" moves. */
export interface MovedYear {
  cycleKey: string;
  periodStart?: string;
  periodEnd?: string;
  /** The year's date as the filing holds it now; absent where the earlier rule has no such year. */
  pinnedDueOn?: string;
  /** The year's date under the updated rule; absent where the updated rule no longer has it. */
  currentDueOn?: string;
  /** CCM-BE24-B: the short year a changed fiscal year end left, as the backend serves it. */
  shortYear?: true;
}

/** A deadline entry that keeps years, with the filing's open years the update moves (none: []). */
export interface DeadlineYearMoves {
  deadlineIndex: number;
  years: MovedYear[];
}

export interface RuleChange {
  obligationId: string;
  pinned: RuleVersionStamp;
  /** The rule's current version; absent when the rule has none (it left the library). */
  current?: RuleVersionStamp;
  flaggedOn?: string;
  /** Whether anything moved between the two versions. */
  changed: boolean;
  aspects: RuleChangeAspect[];
  /**
   * Each version's next worked-out date, per deadline, in the add sheet's form; and, from a server
   * that serves them (CCM-OPS1-REPIN-EVERY-YEAR), every open year the update moves, per deadline
   * entry that keeps years. Absent from an older server, whose comparison is the next dates alone.
   */
  dates: { pinned: WorkedOutDate[]; current: WorkedOutDate[]; yearMoves?: DeadlineYearMoves[] };
}

function movedYearOf(raw: Record<string, unknown>): MovedYear | undefined {
  const cycleKey = optionalStr(raw.cycle_key);
  if (cycleKey === undefined) return undefined;
  const periodStart = optionalStr(raw.period_start);
  const periodEnd = optionalStr(raw.period_end);
  const pinnedDueOn = optionalStr(raw.pinned_due_on);
  const currentDueOn = optionalStr(raw.current_due_on);
  return {
    cycleKey,
    ...(periodStart !== undefined ? { periodStart } : {}),
    ...(periodEnd !== undefined ? { periodEnd } : {}),
    ...(pinnedDueOn !== undefined ? { pinnedDueOn } : {}),
    ...(currentDueOn !== undefined ? { currentDueOn } : {}),
    ...(raw.short_year === true ? { shortYear: true as const } : {}),
  };
}

function yearMovesOf(raw: unknown): DeadlineYearMoves[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return records(raw).flatMap((entry) => {
    const deadlineIndex = optionalInt(entry.deadline_index);
    if (deadlineIndex === undefined) return [];
    const years = records(entry.years).flatMap((year) => {
      const moved = movedYearOf(year);
      return moved !== undefined ? [moved] : [];
    });
    return [{ deadlineIndex, years }];
  });
}

function stampOf(raw: unknown): RuleVersionStamp | undefined {
  if (!isRecord(raw)) return undefined;
  const ruleVersionId = optionalStr(raw.rule_version_id);
  const loadedOn = optionalStr(raw.loaded_on);
  if (ruleVersionId === undefined || loadedOn === undefined) return undefined;
  return { ruleVersionId, version: optionalInt(raw.version) ?? 0, loadedOn };
}

export function decodeRuleChange(body: unknown): RuleChange | undefined {
  if (!isRecord(body)) return undefined;
  const obligationId = optionalStr(body.obligation_id);
  const pinned = stampOf(body.pinned);
  if (obligationId === undefined || pinned === undefined) return undefined;
  const current = stampOf(body.current);
  const flaggedOn = optionalStr(body.flagged_on);
  const dates = isRecord(body.dates) ? body.dates : {};
  const yearMoves = yearMovesOf(dates.year_moves);
  return {
    obligationId,
    pinned,
    ...(current !== undefined ? { current } : {}),
    ...(flaggedOn !== undefined ? { flaggedOn } : {}),
    changed: body.changed === true,
    aspects: records(body.aspects).flatMap((row) => {
      const aspect = optionalStr(row.aspect);
      const deadlineIndex = optionalInt(row.deadline_index);
      return aspect !== undefined
        ? [{ aspect, ...(deadlineIndex !== undefined ? { deadlineIndex } : {}) }]
        : [];
    }),
    dates: {
      pinned: records(dates.pinned).map(decodeWorkedOutDate),
      current: records(dates.current).map(decodeWorkedOutDate),
      ...(yearMoves !== undefined ? { yearMoves } : {}),
    },
  };
}

/** One row of "What changed": the aspect's word, then what each version says. */
export interface ComparisonRow {
  label: string;
  /** The year a "Due date" row is about, where the update moves a year (R1). */
  qualifier?: string;
  before: string;
  now: string;
}

export interface RuleComparison {
  /** The day each version took effect in Givmo's rules ("Before · {date}", "Now · {date}"). */
  pinnedOn: string;
  currentOn: string;
  /** A row per dated aspect, in the backend's order (deadline entries first). */
  rows: ComparisonRow[];
  /** U1-e: what else the update changed, in its words ("wording and sources"); absent for nothing. */
  alsoChanged?: string;
  /** The version shown as "Now": the one "Use the updated rule" moves the filing to. */
  currentRuleVersionId: string;
}

/**
 * The dated aspects: the row each takes, and which versions have the entry (the other reads N48).
 * Each is about one deadline entry, at its `deadline_index`.
 */
const DATED_ASPECTS: Readonly<
  Record<string, { row: 'due' | 'extension'; before: boolean; now: boolean }>
> = {
  deadline_added: { row: 'due', before: false, now: true },
  deadline_removed: { row: 'due', before: true, now: false },
  deadline_changed: { row: 'due', before: true, now: true },
  extension_added: { row: 'extension', before: false, now: true },
  extension_removed: { row: 'extension', before: true, now: false },
  extension_changed: { row: 'extension', before: true, now: true },
};
const UNDATED_ASPECTS: ReadonlySet<string> = new Set(RULE_ASPECT_WORDS.map(([aspect]) => aspect));

/**
 * One version's side of a dated row, or undefined where it isn't served: its worked-out due date,
 * or its extension as the approved extension line words it ("{date}, {condition}.").
 */
function sideOf(row: 'due' | 'extension', entry: WorkedOutDate | undefined): string | undefined {
  if (row === 'due') {
    return entry?.dueOn !== undefined ? displayComplianceDate(entry.dueOn) : undefined;
  }
  const extension = entry?.extension;
  return extension !== undefined
    ? `${displayComplianceDate(extension.extendedDueOn)}, ${extension.condition}.`
    : undefined;
}

/**
 * The comparison as approved words say it, or none (N36 then stands alone): the rule has a current
 * version, something moved, every aspect that moved is in the closed set, each dated one has the
 * dates that say it, and something is left to say once the rows that moved no date are left out. A
 * due date that keeps years says each year the update moves (CCM-OPS1-REPIN-EVERY-YEAR).
 */
export function comparisonOf(change: RuleChange | undefined): RuleComparison | undefined {
  const current = change?.current;
  if (change === undefined || current === undefined || !change.changed) return undefined;
  if (change.aspects.length === 0) return undefined;
  const rows: ComparisonRow[] = [];
  const undated = new Set<string>();
  let dueDateMovedNoYear = false;
  for (const aspect of change.aspects) {
    if (UNDATED_ASPECTS.has(aspect.aspect)) {
      undated.add(aspect.aspect);
      continue;
    }
    const dated = DATED_ASPECTS[aspect.aspect];
    if (dated === undefined) return undefined;
    const index = aspect.deadlineIndex ?? 0;
    // CCM-OPS1-REPIN-EVERY-YEAR: a due date that keeps years is said year by year, every open year
    // the update moves (overdue ones too), from a server that serves them; one that moves none of
    // this filing's years is named by R2's item word instead. An older server, or a deadline that
    // keeps no years, keeps the next dates' row below.
    const years = change.dates.yearMoves?.find((entry) => entry.deadlineIndex === index);
    if (dated.row === 'due' && years !== undefined) {
      if (years.years.length === 0) dueDateMovedNoYear = true;
      // C1-a (D-CCM-112): where the updated rule counts its first year from a sending the register
      // doesn't record yet (D-CCM-110 J-F; the served `first_period`), a year it no longer dates
      // waits on it, rather than being outside the rule.
      const waits = change.dates.current.some(
        (entry) => entry.deadlineIndex === index && waitsOnTheFirstPeriod(entry),
      );
      for (const year of years.years) {
        const before =
          year.pinnedDueOn !== undefined
            ? displayComplianceDate(year.pinnedDueOn)
            : NOT_IN_GIVMOS_RULE;
        const now =
          year.currentDueOn !== undefined
            ? displayComplianceDate(year.currentDueOn)
            : waits
              ? FIRST_PERIOD_WAITS_CELL
              : NOT_IN_GIVMOS_RULE;
        if (before === now) continue;
        // R1, APPROVED (D-CCM-107, Jonathan 2026-10-17 19:29 PDT, "approve recs";
        // CCM-OPS1-REPIN-EVERY-YEAR): the year a "Due date" row is about, in the panel's own year
        // line ("For the fiscal year ended {date}", the Overdue and Next due cards', with Y6 on a
        // short year), under the row's label. A licence's term takes none: it has one row per
        // deadline.
        const qualifier = fiscalYearEndedLine(year);
        rows.push({
          label: DUE_DATE,
          ...(qualifier !== undefined ? { qualifier } : {}),
          before,
          now,
        });
      }
      continue;
    }
    const side = (has: boolean, entries: readonly WorkedOutDate[]) =>
      has
        ? sideOf(
            dated.row,
            entries.find((entry) => entry.deadlineIndex === index),
          )
        : NOT_IN_GIVMOS_RULE;
    const before = side(dated.before, change.dates.pinned);
    const now = side(dated.now, change.dates.current);
    if (before === undefined || now === undefined) return undefined;
    // U1-a is a row for a date that moved: one whose two sides read the same moved nothing for this
    // filing (a month-end landing its year end already meets, an extension's citation alone).
    if (before === now) continue;
    rows.push({ label: dated.row === 'due' ? DUE_DATE : EXTENSION, before, now });
  }
  const words = [
    ...(dueDateMovedNoYear ? [DUE_DATE_ITEM] : []),
    ...RULE_ASPECT_WORDS.filter(([aspect]) => undated.has(aspect)).map(([, word]) => word),
  ];
  // Nothing left that approved words say (a date's rule changed, moving none of this filing's
  // dates, and nothing else): N36 stands alone.
  if (rows.length === 0 && words.length === 0) return undefined;
  return {
    pinnedOn: displayComplianceDate(change.pinned.loadedOn),
    currentOn: displayComplianceDate(current.loadedOn),
    rows,
    ...(words.length > 0 ? { alsoChanged: andList(words) } : {}),
    currentRuleVersionId: current.ruleVersionId,
  };
}

/**
 * N36's day: when Givmo's rule changed (the current version's own day, cut 1008-11's D6), else the
 * day the filing was flagged, where the comparison isn't read or names no current version.
 */
export function ruleUpdatedDay(
  change: RuleChange | undefined,
  flaggedOn: string | undefined,
): string | undefined {
  const day = change?.current?.loadedOn ?? flaggedOn;
  return day !== undefined ? displayComplianceDate(day) : undefined;
}
